import { Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { AdminWalletRow as WalletRecord } from "@/types/wallet";
import { formatINR } from "@/pages/retailer/wallet/data";
import { walletAvailable } from "../data";

const QUICK_AMOUNTS = [500, 1000, 2500, 5000];

const REASON_OPTIONS = [
  { value: "Wallet Recharge", label: "Wallet Recharge" },
  { value: "Promotional Credit", label: "Promotional Credit" },
  { value: "Refund Adjustment", label: "Refund Adjustment" },
  { value: "Correction", label: "Correction" },
  { value: "Other", label: "Other" },
];

interface AddBalanceModalProps {
  open: boolean;
  onClose: () => void;
  wallets: WalletRecord[];
  targetRetailerId: string | null;
  onAdd: (retailerId: string, amount: number, reason: string, remark: string) => void;
  prefillAmount?: string;
  prefillReason?: string;
}

export default function AddBalanceModal({ open, onClose, wallets, targetRetailerId, onAdd, prefillAmount, prefillReason }: AddBalanceModalProps) {
  const [retailerId, setRetailerId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(prefillAmount ?? "");
      setReason(prefillReason ?? "");
      setRemark("");
      setError("");
    }
  }, [open, prefillAmount, prefillReason]);

  const effectiveRetailer = targetRetailerId ?? retailerId;
  const currentWallet = wallets.find((w) => w.retailerId === effectiveRetailer);
  const currentAvailable = currentWallet ? walletAvailable(currentWallet) : 0;
  const parsedAmount = Number(amount) || 0;
  const newBalance = currentAvailable + parsedAmount;

  const retailerOptions = useMemo(
    () =>
      wallets.map((w) => {
        const code = w.userCode && !w.userCode.includes("-") ? w.userCode : "";
        const parts = [
          w.shopName || "Retailer",
          code ? `(${code})` : null,
          w.name && w.name !== w.shopName ? `• ${w.name}` : null,
        ].filter(Boolean);
        return {
          value: w.retailerId,
          label: parts.join(" "),
        };
      }),
    [wallets]
  );

  function reset() {
    setRetailerId("");
    setAmount("");
    setReason("");
    setRemark("");
    setError("");
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit() {
    if (!effectiveRetailer) {
      setError("Please select a retailer");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid credit amount");
      return;
    }
    if (!reason) {
      setError("Please select a reason");
      return;
    }
    if (!remark.trim()) {
      setError("Please add a remark");
      return;
    }
    setError("");
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onAdd(effectiveRetailer, Number(amount), reason, remark.trim());
      handleClose();
    }, 900);
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add balance to wallet"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting}>
            Add Balance
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <SearchableSelect
            label="Retailer"
            required
            value={effectiveRetailer}
            onChange={(val) => setRetailerId(val)}
            options={retailerOptions}
            placeholder="Search retailer by shop, name or ID..."
            disabled={!!targetRetailerId}
          />
          <div className="flex items-center justify-between rounded-lg border border-line bg-slate-50/60 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Current Balance</span>
            <span className="text-sm font-semibold text-ink">{formatINR(currentAvailable)}</span>
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-ink">
              Amount <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-0 top-0 flex h-[42px] w-12 items-center justify-center rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-sm font-medium text-ink">
                ₹
              </span>
              <input
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-14 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(String(a))}
                  className={`inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium transition-colors cursor-pointer ${
                    amount === String(a)
                      ? "border-primary-500 bg-primary-50 text-primary-600"
                      : "border-slate-300 text-ink-secondary hover:border-primary-300 hover:text-primary-500"
                  }`}
                >
                  ₹{a.toLocaleString("en-IN")}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Select
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={REASON_OPTIONS}
          placeholder="Select a reason"
        />

        <Textarea
          label="Remark"
          required
          rows={3}
          maxLength={200}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Add a remark about this credit"
        />
        <p className="-mt-3 text-right text-xs text-ink-muted">{remark.length}/200</p>

        <div className="flex items-center gap-3 rounded-[10px] border border-info-bg bg-info-bg p-4">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-info">
            <Info className="h-5 w-5" />
          </span>
          <div className="flex flex-1 items-center justify-between gap-3">
            <span className="text-[13px] text-ink-secondary">New balance after credit</span>
            <span className="text-base font-semibold text-ink">{formatINR(newBalance)}</span>
          </div>
        </div>

        {error && <p className="text-[13px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}